import type { Maybe } from '@adornis/base/utilTypes.js';
import { Int } from '@adornis/baseql/baseqlTypes.js';
import { Entity, Field } from '@adornis/baseql/decorators.js';
import { MongoEntity } from '@adornis/baseql/entities/mongoEntity.js';
import type { EntityData } from '@adornis/baseql/entities/types.js';
import { getRawCollection } from '@adornis/baseql/server/collections.js';
import { validate } from '@adornis/validation/decorators.js';
import { nonOptional } from '@adornis/validation/functions/nonOptional.js';
import { LevumiMathCategory } from '../../../enums/CampusLevumiEnums.js';
import { CampusLevumiLevel } from './CampusLevumiLevel.js';

@Entity()
export class CampusLevumiItemMath extends MongoEntity {
  public static override _class = 'CampusLevumiItemMath';
  public static override _collectionName = 'campus-levumi-math-items';

  static blankRegex = /\$/g;

  @Field(() => String, { default: v => v ?? LevumiMathCategory.ADD })
  public category!: LevumiMathCategory;

  @validate(nonOptional())
  @Field(() => String)
  public levelID!: string;

  @Field(() => CampusLevumiLevel, {
    resolve: function (this: CampusLevumiItemMath) {
      return async () => {
        const col = await getRawCollection<EntityData<CampusLevumiLevel>>(CampusLevumiLevel._collectionName);
        const res = (await col.findOne({ _id: this.levelID })) as Maybe<CampusLevumiLevel>;
        return res;
      };
    },
  })
  public level!: CampusLevumiLevel;

  @Field(() => Int, { default: v => v ?? 1 })
  public difficulty!: number;

  // example '54 + 3 = $', '$ + 12 = 14'
  @Field(() => String, { default: v => v ?? '' })
  public mathFunction!: string;

  /**
   * Add back whitespaces for better readability
   */
  public getMathFunctionText() {
    let text = this.mathFunction.replace(/=/g, ' $& ');
    text = text.replace(/\+/g, ' $& ');
    text = text.replace(/-/g, ' $& ');
    text = text.replace(/\*/g, ' ⋅ ');
    text = text.replace(/\//g, ' : ');
    return text;
  }

  static evaluateMathFuntionCorrectness(mathFunction: string, answer: number) {
    if (!answer) return false;
    if (typeof answer !== 'number') {
      answer = parseInt(answer);
    }
    // Add answer into function and trim whitspaces
    const joinedFunction = mathFunction.replace(this.blankRegex, answer.toString()).replace(/ /g, '');

    const parts = joinedFunction.split('=');
    if (!parts[0] || !parts[1]) return false;
    const f1 = new Function('return ' + `Math.round(${parts[0]})`);
    const f2 = new Function('return ' + `Math.round(${parts[1]})`);
    return f1() === f2();
  }
}
